import { ReactElement, useEffect } from 'react';

import { ChakraTabList, ChakraTabs, Tab } from '@equip.health/ui';
import { Prompt } from 'react-router-dom';

import { GroupClassEvents } from '~/analytics/groupClassEvents';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';

import { PortalComponent } from '../../../lib/contexts/ComponentPortalContext';
import useTabs from '../../../lib/hooks/useTabs';
import DiscardChangesModalV2 from '../DiscardChangesModalV2';

interface TabsProps<T extends string | number | symbol> {
  basePath: string;
  componentPortalContextKey?: string;
  defaultPath: string;
  getTabIndex: (path: string) => number;
  Panels: () => ReactElement;
  portalComponent?: PortalComponent;
  tabsDictionary: Record<T, TabDefinition>;
  tabListStyles?: Record<string, string>;
  tabsStyles?: Record<string, string>;
  title: string;
}

const Tabs = <T extends string | number | symbol>({
  basePath,
  componentPortalContextKey,
  defaultPath,
  getTabIndex,
  Panels,
  portalComponent,
  tabsDictionary,
  tabListStyles,
  tabsStyles,
  title,
}: TabsProps<T>) => {
  const TABS_ID = `${title.split(' ').join('-').toLowerCase()}__tabs`;
  const TAB_LIST_ID = `${TABS_ID}__tab-list`;

  const {
    cancelNavigation,
    confirmNavigation,
    hasUnsavedChanges,
    isVisible,
    onNavigate,
    onTabChange,
    onTabIndexChange,
    tabIndex,
  } = useTabs({
    basePath,
    componentPortalContextKey,
    defaultPath,
    getTabIndex,
    portalComponent,
  });
  const { trackEvent } = useAnalytics();
  useEffect(() => {
    if (tabsDictionary[tabIndex]?.subPath === 'groups')
      trackEvent(GroupClassEvents.myGroupsTabButtonClicked, {
        Source: AnalyticsSourceValues.CALENDAR_GROUPS_TAB,
      });
  }, [tabIndex, tabsDictionary]);
  return (
    <>
      <Prompt message={onNavigate} when={hasUnsavedChanges} />
      <DiscardChangesModalV2
        isOpen={isVisible}
        onClose={cancelNavigation}
        onConfirm={confirmNavigation}
      />
      <ChakraTabs
        id={TABS_ID}
        index={tabIndex}
        isLazy
        onChange={onTabIndexChange}
        paddingTop="27.5px"
        variant="soft-rounded"
        {...tabsStyles}
      >
        <ChakraTabList
          background="neutral.background.secondary"
          borderRadius="29px"
          height="42px"
          id={TAB_LIST_ID}
          padding="6px"
          {...tabListStyles}
          marginRight="24px"
        >
          {Object.values(tabsDictionary).map(
            ({ label, subPath, isHidden }) =>
              !isHidden && (
                <Tab
                  isActive={getTabIndex(subPath) == tabIndex}
                  key={subPath}
                  onClick={() => {
                    onTabChange(subPath);
                    getTabIndex(subPath);
                  }}
                  title={label}
                  variant="plain"
                />
              ),
          )}
        </ChakraTabList>
        <Panels />
      </ChakraTabs>
    </>
  );
};

export default Tabs;
